import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { Route as RouteIcon, Plus, LayoutDashboard, Car, Calendar, FileText, LogOut } from 'lucide-react';
import { Button } from '../../components/ui/button';
import { Input } from '../../components/ui/input';
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogTrigger } from '../../components/ui/dialog';
import api from '../../utils/api';
import { toast } from 'sonner';

function AdminRoutes() {
  const navigate = useNavigate();
  const [routes, setRoutes] = useState([]);
  const [loading, setLoading] = useState(true);
  const [open, setOpen] = useState(false);
  const [formData, setFormData] = useState({
    origin: '',
    destination: '',
    distance_km: '',
    duration_hours: '',
    base_price: '',
    stops: '',
  });
  const [creating, setCreating] = useState(false);

  useEffect(() => {
    fetchRoutes();
  }, []);

  const fetchRoutes = async () => {
    try {
      const response = await api.get('/admin/routes');
      setRoutes(response.data);
    } catch (error) {
      toast.error('Failed to fetch routes');
    } finally {
      setLoading(false);
    }
  };

  const handleCreate = async () => {
    if (!formData.origin || !formData.destination || !formData.distance_km || !formData.duration_hours || !formData.base_price) {
      toast.error('Please fill all required fields');
      return;
    }
    setCreating(true);
    try {
      const stops = formData.stops ? formData.stops.split(',').map((s) => s.trim()) : [];
      await api.post('/admin/routes', {
        ...formData,
        distance_km: parseInt(formData.distance_km),
        duration_hours: parseFloat(formData.duration_hours),
        base_price: parseFloat(formData.base_price),
        stops,
      });
      toast.success('Route created successfully');
      setFormData({ origin: '', destination: '', distance_km: '', duration_hours: '', base_price: '', stops: '' });
      setOpen(false);
      fetchRoutes();
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Failed to create route');
    } finally {
      setCreating(false);
    }
  };

  const handleLogout = async () => {
    try {
      await api.post('/auth/logout');
      localStorage.removeItem('token');
      toast.success('Logged out successfully');
      navigate('/login');
    } catch (error) {
      localStorage.removeItem('token');
      navigate('/login');
    }
  };

  return (
    <div className="min-h-screen bg-neutral-background flex">
      <div className="w-64 bg-primary text-white p-6">
        <div className="mb-8">
          <img 
            src="https://customer-assets.emergentagent.com/job_0cf4ab96-b53e-403e-86bf-50fb1ab117ef/artifacts/no4fnv8f_Screenshot_20260105_125050_Samsung%20Notes.jpg"
            alt="Hasaka Tours & Travels"
            className="h-16 w-auto mb-3 brightness-0 invert"
          />
          <h2 className="font-manrope font-bold text-xl">Admin Panel</h2>
        </div>
        <nav className="space-y-2">
          <Link to="/admin" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <LayoutDashboard className="w-5 h-5" />
            Dashboard
          </Link>
          <Link to="/admin/vehicles" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <Car className="w-5 h-5" />
            Vehicles
          </Link>
          <Link to="/admin/routes" className="flex items-center gap-3 px-4 py-3 rounded-sm bg-primary-900 text-white font-medium">
            <RouteIcon className="w-5 h-5" />
            Routes
          </Link>
          <Link to="/admin/schedules" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <Calendar className="w-5 h-5" />
            Schedules
          </Link>
          <Link to="/admin/bookings" className="flex items-center gap-3 px-4 py-3 rounded-sm hover:bg-primary-900 text-slate-200 hover:text-white font-medium">
            <FileText className="w-5 h-5" />
            Bookings
          </Link>
        </nav>
        <div className="mt-auto pt-12">
          <Button onClick={() => navigate('/dashboard')} variant="outline" className="w-full mb-3 border-white text-white hover:bg-white hover:text-primary rounded-sm">
            User Dashboard
          </Button>
          <Button onClick={handleLogout} variant="ghost" className="w-full text-white hover:bg-primary-900 rounded-sm">
            <LogOut className="w-4 h-4 mr-2" />
            Logout
          </Button>
        </div>
      </div>

      <div className="flex-1 p-12">
        <div className="flex justify-between items-center mb-8">
          <div>
            <h1 className="font-manrope font-bold text-4xl text-primary mb-2" data-testid="routes-heading">Routes Management</h1>
            <p className="text-slate-600 font-public-sans text-lg">Manage intercity routes</p>
          </div>
          <Dialog open={open} onOpenChange={setOpen}>
            <DialogTrigger asChild>
              <Button className="bg-secondary text-white hover:bg-secondary-600 rounded-sm px-6 py-3" data-testid="add-route-button">
                <Plus className="w-5 h-5 mr-2" />
                Add Route
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-white max-w-2xl">
              <DialogHeader>
                <DialogTitle className="font-manrope font-bold text-2xl text-primary">Add New Route</DialogTitle>
              </DialogHeader>
              <div className="space-y-4 mt-4">
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Origin</label>
                    <Input
                      value={formData.origin}
                      onChange={(e) => setFormData({ ...formData, origin: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      placeholder="Vijayawada"
                      data-testid="origin-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Destination</label>
                    <Input
                      value={formData.destination}
                      onChange={(e) => setFormData({ ...formData, destination: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      placeholder="Hyderabad"
                      data-testid="destination-input"
                    />
                  </div>
                </div>
                <div className="grid grid-cols-3 gap-4">
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Distance (km)</label>
                    <Input
                      type="number"
                      value={formData.distance_km}
                      onChange={(e) => setFormData({ ...formData, distance_km: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      placeholder="270"
                      data-testid="distance-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Duration (hrs)</label>
                    <Input
                      type="number"
                      step="0.5"
                      value={formData.duration_hours}
                      onChange={(e) => setFormData({ ...formData, duration_hours: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      placeholder="4.5"
                      data-testid="duration-input"
                    />
                  </div>
                  <div>
                    <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Base Price ($)</label>
                    <Input
                      type="number"
                      step="0.01"
                      value={formData.base_price}
                      onChange={(e) => setFormData({ ...formData, base_price: e.target.value })}
                      className="rounded-none border-slate-300 h-10"
                      placeholder="45.00"
                      data-testid="price-input"
                    />
                  </div>
                </div>
                <div>
                  <label className="block text-xs font-medium text-slate-700 mb-1 uppercase tracking-wide">Stops (comma-separated, optional)</label>
                  <Input
                    value={formData.stops}
                    onChange={(e) => setFormData({ ...formData, stops: e.target.value })}
                    className="rounded-none border-slate-300 h-10"
                    placeholder="e.g., Guntur, Nalgonda"
                    data-testid="stops-input"
                  />
                </div>
                <Button
                  onClick={handleCreate}
                  disabled={creating}
                  className="bg-primary text-white hover:bg-primary-900 rounded-sm px-8 py-3 w-full"
                  data-testid="create-route-button"
                >
                  {creating ? 'Creating...' : 'Create Route'}
                </Button>
              </div>
            </DialogContent>
          </Dialog>
        </div>

        {loading ? (
          <div className="flex items-center justify-center py-20">
            <div className="text-center">
              <div className="w-16 h-16 border-4 border-primary border-t-transparent rounded-full animate-spin mx-auto mb-4"></div>
              <p className="text-neutral-text-secondary font-public-sans">Loading routes...</p>
            </div>
          </div>
        ) : (
          <div className="space-y-4">
            {routes.map((route) => (
              <div key={route.route_id} className="bg-white rounded-sm p-6 shadow-sm border border-slate-200" data-testid={`route-${route.route_id}`}>
                <div className="flex items-center justify-between">
                  <div className="flex-1">
                    <h3 className="font-manrope font-bold text-2xl text-primary mb-2">
                      {route.origin} → {route.destination}
                    </h3>
                    <div className="grid grid-cols-4 gap-4 text-sm">
                      <div>
                        <span className="text-slate-500 font-public-sans">Distance:</span>
                        <p className="font-semibold text-primary">{route.distance_km} km</p>
                      </div>
                      <div>
                        <span className="text-slate-500 font-public-sans">Duration:</span>
                        <p className="font-semibold text-primary">{route.duration_hours} hours</p>
                      </div>
                      <div>
                        <span className="text-slate-500 font-public-sans">Base Price:</span>
                        <p className="font-semibold text-secondary">${route.base_price}</p>
                      </div>
                      <div>
                        <span className="text-slate-500 font-public-sans">Stops:</span>
                        <p className="font-semibold text-primary">{route.stops.length > 0 ? route.stops.join(', ') : 'None'}</p>
                      </div>
                    </div>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
}

export default AdminRoutes;